import { useQuery } from "@tanstack/react-query";
import { getPositionAIDecisions, type PositionAIDecisionsResponse } from "@app/services/tradingSystemApi";
import { logger } from "@app/services/logger";

/**
 * Hook to fetch AI decisions and analysis for a position.
 * 
 * Features:
 * - Automatic refetching every 30 seconds (only for active positions)
 * - Error handling with logging
 * - Stale time management
 * - Graceful handling of closed positions (410 Gone) - stops refetching
 * - Graceful handling of missing positions (404 Not Found) - stops refetching
 * 
 * @param positionId - The position ID to fetch decisions for
 * @param enabled - Whether to enable the query (default: true)
 * @param refetchInterval - Interval in milliseconds for refetching (default: 30000)
 */
export function useAIDecisions(
  positionId: string | null,
  enabled: boolean = true,
  refetchInterval: number = 30000
) {
  return useQuery<PositionAIDecisionsResponse, Error>({
    queryKey: ["ai-decisions", positionId],
    queryFn: async () => {
      if (!positionId) {
        throw new Error("Position ID is required");
      }
      try {
        return await getPositionAIDecisions(positionId, 20);
      } catch (error) {
        // Check if error is 410 Gone (position closed) or 404 Not Found (position missing)
        const errorMessage = error instanceof Error ? error.message : String(error);
        const statusCode = (error as Error & { statusCode?: number })?.statusCode;
        
        // SUSTAINABLE SOLUTION: For 410/404/400 errors (normal cases), return empty response instead of throwing
        // This prevents React Query from logging these errors to console
        // while still allowing proper query disabling via refetchInterval and retry
        if (statusCode === 410 || errorMessage.includes("410") || errorMessage.includes("was closed")) {
          // Position was closed - this is normal, log as info and return empty response
          logger.info(`Position ${positionId} was closed - stopping AI decisions fetch`);
          return {
            position_id: positionId,
            symbol: positionId.split("-")[0] || "", // Extract symbol from position ID if possible
            current_decision: null,
            technical_indicators: null,
            decision_history: [],
            total_decisions: 0,
            automated_actions: [],
          } as PositionAIDecisionsResponse;
        } else if (statusCode === 404 || errorMessage.includes("404") || errorMessage.includes("not found")) {
          // Position not found - this is normal when position is closed, log as debug instead of warn
          logger.debug(`Position ${positionId} not found - stopping AI decisions fetch (position may be closed)`);
          return {
            position_id: positionId,
            symbol: positionId.split("-")[0] || "", // Extract symbol from position ID if possible
            current_decision: null,
            technical_indicators: null,
            decision_history: [],
            total_decisions: 0,
            automated_actions: [],
          } as PositionAIDecisionsResponse;
        } else if (statusCode === 400 || errorMessage.includes("Invalid position ID")) {
          // Invalid position ID - happens when the position closed and its "pos-{order_id}"
          // identifier can no longer be resolved to a UUID. Treat as benign: return empty
          // response and stop refetching.
          logger.debug(`Position ${positionId} has an invalid ID format - stopping AI decisions fetch (position likely closed)`);
          return {
            position_id: positionId,
            symbol: positionId.split("-")[0] || "", // Extract symbol from position ID if possible
            current_decision: null,
            technical_indicators: null,
            decision_history: [],
            total_decisions: 0,
            automated_actions: [],
          } as PositionAIDecisionsResponse;
        } else {
          // Other errors - log as error and throw (these are real errors)
          logger.error(
            "Error fetching AI decisions",
            error instanceof Error ? error : new Error(String(error))
          );
          throw error;
        }
      }
    },
    enabled: enabled && !!positionId,
    refetchInterval: (query) => {
      // SUSTAINABLE SOLUTION: Stop refetching if position is closed (410) or missing (404)
      // Check both error state and data state (empty response indicates closed/missing position)
      const error = query.state.error;
      const data = query.state.data;
      
      // If we have an empty response (returned for 410/404), stop refetching
      // Check for empty response: current_decision is null AND decision_history is empty AND total_decisions is 0
      if (
        data &&
        data.current_decision === null &&
        data.decision_history.length === 0 &&
        data.total_decisions === 0
      ) {
        return false;
      }
      
      // Also check error state for backward compatibility
      if (error) {
        const errorMessage = error.message;
        const statusCode = (error as Error & { statusCode?: number })?.statusCode;
        
        // Stop refetching for closed positions (410), missing positions (404),
        // and invalid position IDs (400 - position closed and "pos-{order_id}" no longer resolves)
        if (
          statusCode === 410 ||
          statusCode === 404 ||
          statusCode === 400 ||
          errorMessage.includes("410") ||
          errorMessage.includes("404") ||
          errorMessage.includes("was closed") ||
          errorMessage.includes("not found") ||
          errorMessage.includes("Invalid position ID")
        ) {
          return false;
        }
      }
      return refetchInterval;
    },
    staleTime: 15000,
    retry: (failureCount, error) => {
      // SUSTAINABLE SOLUTION: Don't retry on 400/404/410 errors (position closed, missing, or invalid ID)
      const errorMessage = error.message;
      const statusCode = (error as Error & { statusCode?: number })?.statusCode;
      
      if (
        statusCode === 400 ||
        statusCode === 404 ||
        statusCode === 410 ||
        errorMessage.includes("404") ||
        errorMessage.includes("410") ||
        errorMessage.includes("not found") ||
        errorMessage.includes("was closed") ||
        errorMessage.includes("Invalid position ID")
      ) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

